import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import { parseOhlcCsv, toCustomSeries, MIN_ROWS } from '../utils/parseOhlcCsv';

/**
 * THE UPLOAD AFFORDANCE for a run on the customer's own prices.
 *
 * <h2>The file never leaves this component except as parsed bars</h2>
 *
 * It is read with `FileReader`, parsed in memory, and the text is dropped. Nothing is written to
 * `localStorage`, `sessionStorage` or IndexedDB, and the `File` handle is not retained — so a
 * reload loses the data, which is the correct behaviour for a series the platform has promised not
 * to keep. The only thing that outlives the parse is the bar array held in React state, and that
 * dies with the component.
 *
 * <p>This matters more than it sounds. The chart's CTS dataset reader caches to IndexedDB, and
 * reusing any of that machinery here would quietly persist a hedge fund's price history on their
 * analyst's laptop.
 *
 * <h2>Parse first, upload second</h2>
 *
 * Every rule is checked in the browser before a byte is sent — not because the server does not
 * check (it does, and must), but because the alternative is waiting for a ten-megabyte round trip
 * to be told something we already knew. The error list is the product here: a row number and a
 * reason, for up to ten problems at once, so a malformed export is fixed in one pass rather than
 * ten.
 */
const CustomDataUpload = ({ onSeriesReady, onClear, disabled }) => {
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState([]);
  const [summary, setSummary] = useState(null);
  const [fileName, setFileName] = useState('');
  const inputRef = useRef(null);

  const reset = useCallback(() => {
    setErrors([]);
    setSummary(null);
    setFileName('');
    if (inputRef.current) inputRef.current.value = '';
    if (onClear) onClear();
  }, [onClear]);

  const ingest = useCallback((file) => {
    if (!file) return;
    reset();
    setFileName(file.name);
    setBusy(true);

    const reader = new FileReader();
    reader.onerror = () => {
      setBusy(false);
      setErrors([`The file could not be read (${reader.error?.name || 'unknown error'}).`]);
    };
    reader.onload = () => {
      // Parsing a large file blocks the main thread for a moment. Deliberately not moved to a
      // worker: a 20,000-row file parses in a few milliseconds, and a worker would mean the price
      // data crossing another boundary for no measurable benefit.
      try {
        const result = parseOhlcCsv(String(reader.result || ''));
        if (!result.ok) {
          setErrors(result.errors);
          setSummary(result.meta);
          if (onClear) onClear();
          return;
        }
        setSummary(result.meta);
        setErrors([]);
        onSeriesReady(toCustomSeries(result.bars), result.meta);
      } catch (e) {
        // A parser throw is a defect rather than a bad file, so it says so rather than blaming
        // the upload.
        setErrors([`The file could not be parsed: ${e.message}`]);
        if (onClear) onClear();
      } finally {
        setBusy(false);
      }
    };
    reader.readAsText(file);
  }, [onSeriesReady, onClear, reset]);

  const onDrop = useCallback((event) => {
    event.preventDefault();
    setDragging(false);
    if (disabled) return;
    ingest(event.dataTransfer?.files?.[0]);
  }, [ingest, disabled]);

  const ready = summary && !errors.length;

  return (
    <Box sx={{ mt: 1 }}>
      <Box
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => !disabled && inputRef.current?.click()}
        sx={{
          border: '2px dashed',
          borderColor: dragging ? 'primary.main' : (ready ? 'success.light' : 'divider'),
          borderRadius: 2,
          p: 2.5,
          textAlign: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.55 : 1,
          bgcolor: dragging ? 'action.hover' : 'background.paper',
          transition: 'border-color 120ms, background-color 120ms',
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv,text/plain"
          hidden
          onChange={(e) => ingest(e.target.files?.[0])}
          data-testid="custom-data-file-input"
        />
        <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
          {dragging ? 'Drop the CSV to load it' : 'Drag a CSV here, or click to choose a file'}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
          A header row naming date, open, high, low, close and (optionally) volume. Columns are
          matched by name, so the order does not matter and extra columns are ignored.
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
          Dates must be <code>yyyy-MM-dd</code>, oldest first, with no repeats.
          At least {MIN_ROWS} rows.
        </Typography>
      </Box>

      {busy && <LinearProgress sx={{ mt: 1 }} />}

      {fileName && !busy && (
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            label={fileName}
            color={ready ? 'success' : 'default'}
            variant={ready ? 'filled' : 'outlined'}
          />
          {ready && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {summary.rows.toLocaleString()} bars · {summary.firstDate} → {summary.lastDate}
              {summary.hasVolume ? '' : ' · no volume column (treated as 0)'}
            </Typography>
          )}
          <Button size="small" onClick={reset} sx={{ ml: 'auto' }}>Clear</Button>
        </Box>
      )}

      {ready && (
        <Alert severity="success" sx={{ mt: 1 }} data-testid="custom-data-ready">
          Loaded in this browser only. The series is sent with each run and is never stored — a
          page reload clears it.
        </Alert>
      )}

      {errors.length > 0 && (
        <Alert severity="error" sx={{ mt: 1 }} data-testid="custom-data-errors">
          <AlertTitle sx={{ fontSize: 13 }}>
            {/* The short-series case is a requirement, not a malformed file, so it reads as one. */}
            {summary && summary.rows > 0 && summary.rows < MIN_ROWS
              ? `Only ${summary.rows.toLocaleString()} rows — ${MIN_ROWS} are needed`
              : 'This file cannot be used'}
          </AlertTitle>
          <Box component="ul" sx={{ m: 0, pl: 2.5, fontSize: 12.5 }}>
            {errors.map((message, i) => (
              // Index keys are safe here: the list is replaced wholesale on every parse and never
              // reordered or edited in place.
              // eslint-disable-next-line react/no-array-index-key
              <li key={i}>{message}</li>
            ))}
          </Box>
        </Alert>
      )}
    </Box>
  );
};

CustomDataUpload.propTypes = {
  /**
   * Called with `({ bars }, meta)` once a file satisfies EVERY rule, and not before.
   *
   * <p>There is deliberately no "partial" callback. A series that fails one row is not usable at
   * reduced confidence — it is a file whose bar count disagrees with the customer's spreadsheet —
   * so the host either has a complete series or has none.
   */
  onSeriesReady: PropTypes.func.isRequired,
  /**
   * Called whenever the loaded series stops being valid, so the host drops it too.
   *
   * <p>Fired on a failed parse as well as on an explicit Clear. Without that, choosing a bad file
   * after a good one would leave the previous series live while the panel showed errors — and the
   * next run would quietly use data the reader believed they had replaced.
   */
  onClear: PropTypes.func,
  disabled: PropTypes.bool,
};

CustomDataUpload.defaultProps = {
  onClear: undefined,
  disabled: false,
};

export default CustomDataUpload;
